import React, { memo, useCallback, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import pathnames from "routes/pathnames";

import formatIndexNumber from "common/format-index-number";

import CONSTANTS from "constants/constant";
import { statusOptions } from "constants/status-options";

import PopupAdsTableProps from "dto/components/pages/popup-ads/page-popup-ads-table-dto";

import AppPagination from "components/app-pagination";

import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";

const PopupAdsTable: React.FC<PopupAdsTableProps> = ({ ads, paginationTable, fetching, onPageChange }) => {
	const navigate = useNavigate();
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const isEmptyData = useMemo(() => ads.length === 0 && !fetching, [ads.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        navigate(pathnames.popupAdsDetails + id);
    }, [navigate]);

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		return options.find((option) => option.value === value)?.label || value;
	};

	return (
		<div className="app-popup-ads-table">
			<div className="ads-table">
				<div className="ads-table__wrapper">
					<table className="ads-table__container">
						<thead className="ads-table__header">
							<tr>
								<th className="ads-table__th">No.</th>

								<th className="ads-table__th">Title</th>

								<th className="ads-table__th">Description</th>

								<th className="ads-table__th">Start Date</th>

								<th className="ads-table__th">End Date</th>

								<th className="ads-table__th">Status</th>
							</tr>
						</thead>

						<tbody className="ads-table__content">
							{!isEmptyData &&
								ads.map((ad, index) => {
									const isExpanded = expandedRows.includes(ad.id);

									return (
										<tr className="ads-table__tr" key={ad.id}>
											<td className="ads-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

											<td className="ads-table__td">{isExpanded ? ad.title : truncateText(ad.title, 30)}</td>

											<td className="ads-table__td">{isExpanded ? ad.description : truncateText(ad.description, 60)}</td>

											<td className="ads-table__td">{moment(ad.startAt).format(CONSTANTS.LISTING_DATE_FORMAT)}</td>

											<td className="ads-table__td">{moment(ad.endAt).format(CONSTANTS.LISTING_DATE_FORMAT)}</td>

											<td className={ad.status === "active" ? "ads-table__td--active" : "ads-table__td--inactive"}>{getLabel(statusOptions, ad.status)}</td>

											<td className="ads-table__td">
												<div className="ads-table__actions">
													<button className="ads-table__edit-button" onClick={() => toggleRow(ad.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="ads-table__edit-button" onClick={() => onHandleEdit(ad.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											</td>
										</tr>
									);
								})}

							{fetching && (
								<tr className="ads-table__tr">
									<td className="ads-table__message" colSpan={7}>
										Loading...
									</td>
								</tr>
							)}

							{isEmptyData && (
								<tr className="ads-table__tr">
									<td className="ads-table__message" colSpan={7}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && <AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onPageChange} numberOfElements={paginationTable.numberOfElements} />}
			</div>
		</div>
	);
};

export default memo(PopupAdsTable);
